import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { message, Form } from 'antd';
import { useForgotPasswordMutation } from '../../store/services/authService';
import { useNavigate, Link } from 'react-router-dom';
import TitleText from '../atoms/TitleText';
import InfoText from '../atoms/InfoText';
import { AuthWrapper } from './AuthWrapper';
import Input from '../atoms/Input';
import Button from '../atoms/Button';

const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'This field cannot be empty')
    .email('Please enter a valid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPassword() {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'all',
  });

  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const navigate = useNavigate();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    const newData = { ...data, otpType: 'FORGET_PASSWORD' } as const;
    try {
      await forgotPassword(newData).unwrap();
      message.success('OTP has been sent to your email address!');
      navigate(`/verify-otp?email=${encodeURIComponent(data.email)}`);
    } catch (error: any) {
      message.error(
        error?.data?.errors?.default ||
          Object.values(error?.data?.errors || {}).join(', ')
      );
    }
  };

  return (
    <AuthWrapper>
      <div className="w-full max-w-xl space-y-8" data-test="forgot-password-page">
        <div className="">
          <TitleText
            text="Forgot your password?"
            className="!text-2xl !font-bold mb-3"
          />
          <InfoText
            text="Tell us more about your workspace so we can provide you a personalized experience tailored to your needs and preferences."
            size="xs"
          />
        </div>

        {/* Forgot Password Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="mt-8" data-test="forgot-password-form">
          {/* Email Field */}
          <Form.Item
            label={
              <span className="text-gray-900 font-medium text-sm">E-mail</span>
            }
            className="mb-6"
            layout="vertical"
          >
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  id="email"
                  value={field.value || ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="info@mail.com"
                  autoComplete="email"
                  status={errors.email ? 'error' : undefined}
                  helperText={errors.email?.message}
                  className="rounded-lg border-gray-300"
                  data-test="forgot-password-email-input"
                />
              )}
            />
          </Form.Item>

          <Form.Item className="">
            <Button
              type="submit"
              variant="dark"
              disabled={isLoading || isSubmitting}
              className="w-full font-semibold h-12"
              data-test="forgot-password-submit-btn"
            >
              {isLoading || isSubmitting ? 'Sending...' : 'Send OTP'}
            </Button>
          </Form.Item>

          <div className="flex items-center gap-1 justify-center">
            <InfoText text="Already have an account?" size="sm" />
            <Link
              to="/login"
              className="text-gray-900 hover:text-gray-700 text-sm font-medium underline"
              data-test="forgot-password-signin-link"
            >
              Sign In
            </Link>
          </div>
        </form>
      </div>
    </AuthWrapper>
  );
}
